import { useAuth } from '@/contexts/AuthContext';
import { useGamification } from '@/contexts/GamificationContext';
import { getMasterLeaderboard, getGritLeaderboard, getRocketLeaderboard, getHallOfFameLeaderboard, getPointHistory, getMonthlyProgress, getPreviousMonthWinners } from '@/services/generatedAPIs';
import { Leaderboard, PointsSystem, UserInfoResponseRole, MultiplierStatusResponse, MonthlyAchievementProgressResponse, LeaderboardWinnersResponse } from '@/services/generatedAPIs.schemas';
import { ROUTE } from '@/routes/constants';
import { Trophy, Clock, Zap, BookOpen, CheckCircle, Star, TrendingUp, Loader2, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';
import { forwardRef, useCallback, useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { useAsyncAction } from '@/hooks/use-async-action';
import { useLocation } from 'react-router';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import MultiplierBookIcon from './MultiplierBookIcon';
import { useTranslation } from 'react-i18next';
import { format, subMonths } from 'date-fns';
// 三个积分图标（1:1 取自 Figma 隨心練 设计稿，矢量）：MPL 闪电 / KP 星 / GS 钻石
import MplBoltIcon from '@/assets/practice/mpl-bolt.svg';
import KpStarIcon from '@/assets/practice/kp-star.svg';
import GsDiamondIcon from '@/assets/practice/gs-diamond.svg';
// 排行榜专用图标（取自 Figma 1920 排行榜弹窗）：标题奖杯（淡蓝） / 名次徽章（深灰）
import TrophyIcon from '@/assets/leaderboard/trophy.svg';
import MedalIcon from '@/assets/leaderboard/medal.svg';

type LeaderboardRank = 1 | 2 | 3;

// 名次配色（Figma 1920 排行榜弹窗）：#1 黄 / #2 灰 / #3 橙；边框 = 徽章方块底色（同色饱和版）。
const leaderboardRankStyles: Record<LeaderboardRank, { border: string; medalBg: string }> = {
  1: { border: 'border-yellow-300', medalBg: 'bg-yellow-300' },
  2: { border: 'border-slate-300', medalBg: 'bg-slate-300' },
  3: { border: 'border-orange-300', medalBg: 'bg-orange-300' },
};

// 弹窗内徽章方块（黄/灰/橙底 + 深灰 medal svg）：尺寸随屏幕略调。
const LeaderboardMedal = ({ rank }: { rank: LeaderboardRank }) => (
  <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl xl:h-9 xl:w-9 [@media(max-height:500px)]:h-[18px] [@media(max-height:500px)]:w-[18px] [@media(max-height:500px)]:rounded-[5px] ${leaderboardRankStyles[rank].medalBg}`}>
    <img src={MedalIcon} alt="" aria-hidden className="h-4 w-auto xl:h-[18px] [@media(max-height:500px)]:h-[9px]" />
  </span>
);

// 校内/传奇榜单的一行（白底 + 名次色描边）。
const LeaderboardRow = ({ rank, name, score }: { rank: LeaderboardRank; name: string; score: string }) => (
  <div className={`flex items-center justify-between rounded-2xl border-[1.5px] bg-white px-3 py-2.5 xl:px-4 xl:py-3 [@media(max-height:500px)]:rounded-md [@media(max-height:500px)]:px-2 [@media(max-height:500px)]:py-2 ${leaderboardRankStyles[rank].border}`}>
    <div className="flex min-w-0 items-center gap-3 [@media(max-height:500px)]:gap-2">
      <LeaderboardMedal rank={rank} />
      <span className="shrink-0 text-sm font-medium tabular-nums text-foreground xl:text-base [@media(max-height:500px)]:text-xs">#{rank}</span>
      <span className="truncate text-sm text-foreground xl:text-base [@media(max-height:500px)]:text-xs">{name}</span>
    </div>
    <span className="ml-3 shrink-0 text-sm font-medium tabular-nums text-foreground xl:text-base [@media(max-height:500px)]:ml-2 [@media(max-height:500px)]:text-xs">{score}</span>
  </div>
);


const eventTypeIcons: Record<string, typeof Zap> = {
  submit_correct: CheckCircle,
  mistake_correction: TrendingUp,
  complete_homework: BookOpen,
  homework_all_correct: Star,
  self_assessment: Zap,
};

// Monthly progress section used inside KP/GS dialogs
const MonthlyProgressSection = ({
  monthlyProgress,
  isLoading,
  type,
}: {
  monthlyProgress: MonthlyAchievementProgressResponse | null;
  isLoading: boolean;
  type: 'KP' | 'GS';
}) => {
  const { t } = useTranslation();
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span className="text-xs">{t('common.loading')}</span>
      </div>
    );
  }
  if (!monthlyProgress) return null;

  if (type === 'KP') {
    const total = monthlyProgress.totalAssignments ?? 0;
    const count = monthlyProgress.perfectCount ?? 0;
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t('gamification.perfectProgress', { defaultValue: 'Perfect scores' })}</span>
          <span className="font-medium">{count}/{total}</span>
        </div>
        <div className="h-2 w-full rounded-full bg-muted">
          <div
            className="h-2 rounded-full bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-400 transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
        {monthlyProgress.perfectEligible ? (
          <div className="flex items-center gap-1 text-xs text-violet-600 font-medium">
            <CheckCircle2 className="h-3 w-3" />
            {t('gamification.perfectBonus', { defaultValue: '30 KP Bonus Earned!' })}
          </div>
        ) : (
          <p className="text-[10px] text-muted-foreground">
            {t('gamification.perfectBonusNeeded', { defaultValue: 'Get perfect scores to earn 30 KP' })}
          </p>
        )}
      </div>
    );
  }

  // GS type
  const total = monthlyProgress.totalAssignments ?? 0;
  const count = monthlyProgress.onTimeCount ?? 0;
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{t('gamification.onTimeProgress', { defaultValue: 'On-time submissions' })}</span>
        <span className="font-medium">{count}/{total}</span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-400 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
      {monthlyProgress.onTimeEligible ? (
        <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
          <CheckCircle2 className="h-3 w-3" />
          {t('gamification.onTimeBonus', { defaultValue: '15 GS Bonus Earned!' })}
        </div>
      ) : (
        <p className="text-[10px] text-muted-foreground">
          {t('gamification.onTimeBonusNeeded', { defaultValue: 'Keep submitting on time to earn 15 GS' })}
        </p>
      )}
    </div>
  );
};

const PointHistoryList = ({ currencyType }: { currencyType: 'KP' | 'GS' }) => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<PointsSystem[]>([]);
  const { isLoading, execute } = useAsyncAction({ showErrorToast: false });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchHistory = useCallback((pageNum: number) => {
    execute(async () => {
      try {
        const rsp = await getPointHistory({ page: pageNum, size: 20 });
        if (rsp.code === 200 && rsp.data?.transactions) {
          const filtered = rsp.data.transactions.filter(tx => tx.currencyType === currencyType);
          if (pageNum === 1) {
            setTransactions(filtered);
          } else {
            setTransactions(prev => [...prev, ...filtered]);
          }
          setHasMore((rsp.data.page ?? 1) * (rsp.data.size ?? 20) < (rsp.data.total ?? 0));
        }
      } catch (error) {
        console.warn('Failed to fetch point history:', error);
      }
    });
  }, [currencyType, execute]);

  useEffect(() => {
    fetchHistory(1);
  }, [fetchHistory]);

  if (isLoading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        {t('common.loading')}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-xs text-muted-foreground py-4 text-center">
        {t('gamification.pointHistory.empty', { defaultValue: 'No point history yet' })}
      </div>
    );
  }

  return (
    <ScrollArea className="h-72">
    <div className="space-y-2">
      {transactions.map((tx, idx) => {
        const Icon = eventTypeIcons[tx.eventType ?? ''] ?? Zap;
        return (
          <div key={tx.pointId ?? idx} className="flex items-center justify-between rounded-lg border border-muted-foreground/10 bg-muted/20 px-3 py-2">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-xs font-medium text-foreground">{tx.eventType ? t(`gamification.event.${tx.eventType}`, { defaultValue: tx.description || tx.eventType }) : tx.description}</div>
                {tx.awardedAt && (
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {format(new Date(tx.awardedAt), 'yyyy-MM-dd HH:mm')}
                  </div>
                )}
              </div>
            </div>
            <span className={`text-sm font-bold ${(tx.pointsAmount ?? 0) >= 0 ? (currencyType === 'KP' ? 'text-violet-500' : 'text-amber-500') : 'text-red-500'}`}>
              {(tx.pointsAmount ?? 0) >= 0 ? '+' : ''}{tx.pointsAmount}
            </span>
          </div>
        );
      })}
      {hasMore && (
        <button
          onClick={() => {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchHistory(nextPage);
          }}
          className="w-full text-center text-xs text-primary hover:underline py-2"
          disabled={isLoading}
        >
          {isLoading ? t('common.loading') : t('common.loadMore', { defaultValue: 'Load more' })}
        </button>
      )}
    </div>
    </ScrollArea>
  );
};

type StudentGamificationScoreProps = {
  kp: number;
  gs: number;
  kpAllTime: number;
  gsAllTime: number;
  multiplierStatus: MultiplierStatusResponse | null;
  monthlyProgress: MonthlyAchievementProgressResponse | null;
  isMonthlyProgressLoading: boolean;
  currentMonth: string;
  onRequestMonthlyProgress: () => void;
};

/**
 * 单个积分列（Figma 隨心練 右上数据卡）：彩色小标签在上，下方为彩色图标 + 大号深色数字。
 * 用 forwardRef 透传 Radix DialogTrigger(asChild) 注入的 ref / onClick / aria 等属性。
 */
const MetricColumn = forwardRef<
  HTMLButtonElement,
  { label: string; color: string; icon: string; value: ReactNode; ariaLabel?: string } & ButtonHTMLAttributes<HTMLButtonElement>
>(({ label, color, icon, value, ariaLabel, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={ariaLabel}
    className="flex cursor-pointer flex-col items-start gap-1 rounded-2xl px-1.5 py-1 text-left transition hover:bg-black/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:gap-1.5 sm:px-2 [@media(max-height:500px)]:gap-0.5 [@media(max-height:500px)]:px-0 [@media(max-height:500px)]:py-0 [@media(max-height:500px)]:rounded-lg"
    {...props}
  >
    <span className="font-sans text-[11px] font-semibold leading-none tracking-wide sm:text-[12px] min-[1920px]:text-[13px]! [@media(max-height:500px)]:text-[12px]" style={{ color }}>{label}</span>
    <span className="flex items-center gap-1 sm:gap-1.5 [@media(max-height:500px)]:gap-[3px]">
      <img src={icon} alt="" aria-hidden className="h-[18px] w-auto shrink-0 sm:h-[19px] min-[1920px]:h-[22px]! [@media(max-height:500px)]:h-[12px]" />
      <span className="font-sans text-[20px] font-extrabold leading-none tabular-nums text-[#2A2F32] sm:text-[22px] min-[1920px]:text-[26px]! [@media(max-height:500px)]:text-[13px]">{value}</span>
    </span>
  </button>
));
MetricColumn.displayName = 'MetricColumn';

const StudentGamificationScore = ({
  kp,
  gs,
  kpAllTime,
  gsAllTime,
  multiplierStatus,
  monthlyProgress,
  isMonthlyProgressLoading,
  currentMonth,
  onRequestMonthlyProgress,
}: StudentGamificationScoreProps) => {
  const { t } = useTranslation();

  const isBonus = multiplierStatus?.homeworkComplete === true;
  const multiplierValue = multiplierStatus?.currentMultiplier;

  return (
    <div className="font-yusei-magic flex items-stretch gap-3 sm:gap-4 lg:gap-5 min-[1920px]:gap-7! [@media(max-height:500px)]:h-full [@media(max-height:500px)]:w-full [@media(max-height:500px)]:items-center [@media(max-height:500px)]:justify-between [@media(max-height:500px)]:gap-0" aria-label={`Knowledge Points ${kp}, Growth Spark ${gs}`}>
      {/* MPL 倍率 */}
      {multiplierStatus && (
        <Dialog>
          <DialogTrigger asChild>
            <MetricColumn
              label="MPL"
              color="#7DD02A"
              icon={MplBoltIcon}
              value={multiplierValue != null ? `${multiplierValue}x` : '—'}
              ariaLabel="Practice multiplier info"
            />
          </DialogTrigger>
          <DialogContent className="sm:max-w-sm [@media(max-height:500px)]:!max-w-[520px] [@media(max-height:500px)]:!max-h-[90vh] [@media(max-height:500px)]:overflow-y-auto [@media(max-height:500px)]:!p-4 [@media(max-height:500px)]:!gap-2 [@media(max-height:500px)]:!rounded-[18px]" style={{ fontFamily: 'var(--font-sans)' }}>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold flex items-center gap-2 [@media(max-height:500px)]:!text-sm [@media(max-height:500px)]:!gap-1.5">
                <MultiplierBookIcon variant={isBonus ? 'pos' : 'neg'} idSuffix="dlg" className="h-8 w-8 [@media(max-height:500px)]:!h-6 [@media(max-height:500px)]:!w-6" animated={false} />
                {t('gamification.multiplierTitle', { defaultValue: 'Practice Point Multiplier' })}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm [@media(max-height:500px)]:!space-y-2">
              {/* Current status */}
              <div className={`rounded-xl p-4 [@media(max-height:500px)]:!p-2.5`}>
                <div className={`text-3xl font-bold mb-1 [@media(max-height:500px)]:!text-2xl ${isBonus ? 'text-emerald-700' : 'text-red-600'}`}>
                  ×{multiplierValue}
                </div>
                <div className={`text-base font-medium mt-3 [@media(max-height:500px)]:!text-sm [@media(max-height:500px)]:!mt-1.5 ${isBonus ? 'text-emerald-700' : 'text-red-600'}`}>
                  {isBonus
                    ? t('gamification.multiplierActive', { defaultValue: '1.5x Bonus Active!' })
                    : t('gamification.multiplierInactive', { defaultValue: 'Homework incomplete — points reduced' })
                  }
                </div>
              </div>
              <p className="text-muted-foreground [@media(max-height:500px)]:!text-xs">
                {t('gamification.multiplierExplain', { defaultValue: 'Your practice point earnings are multiplied by this value. Complete all homework assignments by their deadline to earn the bonus multiplier.' })}
              </p>
              <div className="space-y-2 [@media(max-height:500px)]:!space-y-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                  <span>{t('gamification.multiplierBonusRule', { defaultValue: 'Homework complete → ×1.5 bonus on practice points' })}</span>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="inline-block w-2 h-2 rounded-full bg-red-500" />
                  <span>{t('gamification.multiplierPenaltyRule', { defaultValue: 'Homework incomplete → ×0.5 (points halved)' })}</span>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* KP Score Button */}
      <Dialog onOpenChange={(open) => { if (open) onRequestMonthlyProgress(); }}>
        <DialogTrigger asChild>
          <MetricColumn
            label="KP"
            color="#DB4B16"
            icon={KpStarIcon}
            value={kp.toLocaleString()}
            ariaLabel="Knowledge Power details"
          />
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto [@media(max-height:500px)]:!max-w-[560px] [@media(max-height:500px)]:!max-h-[90vh] [@media(max-height:500px)]:!p-4 [@media(max-height:500px)]:!gap-2 [@media(max-height:500px)]:!rounded-[18px]" style={{ fontFamily: 'var(--font-sans)' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold [@media(max-height:500px)]:!text-base">{t('component.kp')} (KP)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground [@media(max-height:500px)]:!space-y-2">
            <p>{t('component.kpDescription')}</p>

            {/* KP Stats */}
            <div className="grid grid-cols-2 gap-3 [@media(max-height:500px)]:!gap-2">
              <div className="rounded-xl bg-muted/40 p-3 text-center [@media(max-height:500px)]:!p-2">
                <div className="text-[10px] text-muted-foreground mb-1">{t('gamification.thisMonth', { defaultValue: 'This Month' })} ({currentMonth})</div>
                <div className="text-2xl font-bold bg-gradient-to-r from-violet-500 to-indigo-400 bg-clip-text text-transparent [@media(max-height:500px)]:!text-lg">{kp}</div>
              </div>
              <div className="rounded-xl bg-muted/40 p-3 text-center [@media(max-height:500px)]:!p-2">
                <div className="text-[10px] text-muted-foreground mb-1">{t('gamification.allTime', { defaultValue: 'All-Time' })}</div>
                <div className="text-2xl font-bold bg-gradient-to-r from-violet-500 to-indigo-400 bg-clip-text text-transparent [@media(max-height:500px)]:!text-lg">{kpAllTime}</div>
              </div>
            </div>

            {/* Monthly KP achievement */}
            <div className="rounded-xl border border-muted-foreground/20 bg-muted/40 p-3 [@media(max-height:500px)]:!p-2">
              <div className="text-xs font-semibold text-foreground mb-2 [@media(max-height:500px)]:!mb-1">{t('gamification.monthlyKpProgress', { defaultValue: 'Monthly KP Achievement' })}</div>
              <MonthlyProgressSection monthlyProgress={monthlyProgress} isLoading={isMonthlyProgressLoading} type="KP" />
            </div>

            {/* Transaction history */}
            <div className="rounded-xl border border-muted-foreground/20 bg-muted/40 p-4 [@media(max-height:500px)]:!p-2.5">
              <div className="text-sm font-semibold text-foreground mb-2 [@media(max-height:500px)]:!mb-1 [@media(max-height:500px)]:!text-xs">{t('gamification.kpHistory', { defaultValue: 'KP Transaction History' })}</div>
              <PointHistoryList currencyType="KP" />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* GS Score Button */}
      <Dialog onOpenChange={(open) => { if (open) onRequestMonthlyProgress(); }}>
        <DialogTrigger asChild>
          <MetricColumn
            label="GS"
            color="#1F9DC0"
            icon={GsDiamondIcon}
            value={gs.toLocaleString()}
            ariaLabel="Growth Spark details"
          />
        </DialogTrigger>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto [@media(max-height:500px)]:!max-w-[560px] [@media(max-height:500px)]:!max-h-[90vh] [@media(max-height:500px)]:!p-4 [@media(max-height:500px)]:!gap-2 [@media(max-height:500px)]:!rounded-[18px]" style={{ fontFamily: 'var(--font-sans)' }}>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold [@media(max-height:500px)]:!text-base">{t("component.gs")} (GS)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-muted-foreground [@media(max-height:500px)]:!space-y-2">
            <p>{t('component.gsDescription')}</p>

            {/* GS Stats */}
            <div className="grid grid-cols-2 gap-3 [@media(max-height:500px)]:!gap-2">
              <div className="rounded-xl bg-muted/40 p-3 text-center [@media(max-height:500px)]:!p-2">
                <div className="text-[10px] text-muted-foreground mb-1">{t('gamification.thisMonth', { defaultValue: 'This Month' })} ({currentMonth})</div>
                <div className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent [@media(max-height:500px)]:!text-lg">{gs}</div>
              </div>
              <div className="rounded-xl bg-muted/40 p-3 text-center [@media(max-height:500px)]:!p-2">
                <div className="text-[10px] text-muted-foreground mb-1">{t('gamification.allTime', { defaultValue: 'All-Time' })}</div>
                <div className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent [@media(max-height:500px)]:!text-lg">{gsAllTime}</div>
              </div>
            </div>

            {/* Monthly GS achievement */}
            <div className="rounded-xl border border-muted-foreground/20 bg-muted/40 p-3 [@media(max-height:500px)]:!p-2">
              <div className="text-xs font-semibold text-foreground mb-2 [@media(max-height:500px)]:!mb-1">{t('gamification.monthlyGsProgress', { defaultValue: 'Monthly GS Achievement' })}</div>
              <MonthlyProgressSection monthlyProgress={monthlyProgress} isLoading={isMonthlyProgressLoading} type="GS" />
            </div>

            {/* Transaction history */}
            <div className="rounded-xl border border-muted-foreground/20 bg-muted/40 p-4 [@media(max-height:500px)]:!p-2.5">
              <div className="text-sm font-semibold text-foreground mb-2 [@media(max-height:500px)]:!mb-1 [@media(max-height:500px)]:!text-xs">{t('gamification.gsHistory', { defaultValue: 'GS Transaction History' })}</div>
              <PointHistoryList currencyType="GS" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

type LeaderboardData = {
  entries: Leaderboard[];
  studentNames?: { [key: string]: string };
  schoolNamesCn?: { [key: string]: string };
};

// Figma 1920 排行榜弹窗的「分组卡」：淡黄米色底 + 段落标题 + 一组名次行。
// - 校内榜（master/grit/rocket）每段只显示 top-1（设计稿样式）。
// - 传奇榜（hall of fame）显示 top-3，名次配色随之变化。
const LeaderboardSection = ({
  title,
  data,
  isLoading,
  scoreKey,
  isHallOfFame,
  topN = 1,
  className = '',
}: {
  title: string;
  data: LeaderboardData | null;
  isLoading: boolean;
  scoreKey: 'totalKp' | 'totalGs' | 'improvementScore' | 'totalPoints';
  isHallOfFame?: boolean;
  topN?: number;
  className?: string;
}) => {
  const { t } = useTranslation();
  const entries = (data?.entries ?? []).slice(0, topN);

  return (
    <div className={`space-y-3 rounded-2xl bg-background px-4 py-3.5 xl:px-5 xl:py-4 [@media(max-height:500px)]:space-y-1 [@media(max-height:500px)]:rounded-[10px] [@media(max-height:500px)]:px-2 [@media(max-height:500px)]:py-1 ${className}`}>
      <div className="text-sm font-medium text-foreground xl:text-base [@media(max-height:500px)]:text-xs [@media(max-height:500px)]:leading-none">{title}</div>
      {isLoading ? (
        <div className="flex items-center justify-center py-3 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : entries.length === 0 ? (
        <div className="py-3 text-center text-xs text-muted-foreground [@media(max-height:500px)]:py-1">{t('common.noDataYet')}</div>
      ) : (
        <div className="space-y-2.5 [@media(max-height:500px)]:space-y-[9px]">
          {entries.map((entry, idx) => {
            const rank = (idx + 1) as LeaderboardRank;
            const studentId = String(entry.studentId);
            const displayName = data?.studentNames?.[studentId] ?? `Student #${entry.studentId}`;
            const school = data?.schoolNamesCn?.[studentId] ?? entry.schoolName;
            const name = isHallOfFame && school ? `${displayName} (${school})` : displayName;
            const score = String(entry[scoreKey] ?? 0);
            return <LeaderboardRow key={entry.id ?? idx} rank={rank} name={name} score={score} />;
          })}
        </div>
      )}
    </div>
  );
};

/**
 * 学生端 MPL/KP/GS 白色数据卡（Figma 隨心練 右上角）。
 * 排行榜按钮已拆分到 GamificationLeaderboard（置于顶栏控制行）。
 */
export default function GamificationHeader() {
  const { t } = useTranslation();
  const { getCurrentUser } = useAuth();
  const { kp, gs, kpAllTime, gsAllTime, currentMonth: contextMonth, gamificationEnabled, multiplierStatus, refreshMultiplier } = useGamification();
  const userRole = getCurrentUser()?.role;
  const isStudent = userRole === UserInfoResponseRole.STUDENT;

  const location = useLocation();

  // Monthly progress — fetched lazily when KP or GS dialog is first opened
  const [monthlyProgress, setMonthlyProgress] = useState<MonthlyAchievementProgressResponse | null>(null);
  const [isMonthlyProgressLoading, setIsMonthlyProgressLoading] = useState(false);
  const monthlyProgressFetched = useRef(false);

  const currentMonth = contextMonth || format(new Date(), 'yyyy-MM');

  // 进入内页时静默刷新倍率:值存于 GamificationContext(挂应用根,跨页面切换持续),
  // refreshMultiplier 拿到新值才原地替换、从不清回 null。Header 随各页 ContentLayout 重挂时
  // 直接沿用 context 里上次的值,后台再静默刷新 → 不再出现 MPL 列先消失再出现的闪烁。
  useEffect(() => {
    if (!isStudent) return;
    refreshMultiplier();
  }, [isStudent, location.pathname, refreshMultiplier]);

  // Show multiplier toast once when student enters practice home page
  const multiplierToastShown = useRef<string | null>(null);
  useEffect(() => {
    if (location.pathname !== ROUTE.STUDENT.PRACTICE_HOME) {
      multiplierToastShown.current = null;
      toast.dismiss('multiplier-toast');
      return;
    }
    if (!isStudent || !gamificationEnabled || !multiplierStatus || multiplierToastShown.current === location.pathname) return;
    multiplierToastShown.current = location.pathname;
    if (multiplierStatus.homeworkComplete === false) {
      toast(t('practice.multiplierWarningTitle', { defaultValue: 'Reduced Points Active (×0.5)' }), {
        id: 'multiplier-toast',
        description: t('practice.multiplierWarningDesc', { defaultValue: 'You have incomplete homework. Practice points are halved until all homework is submitted.' }),
        duration: 5000,
        style: { backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c', width: '350px' },
        classNames: { description: '!text-red-800' },
      });
    } else if (multiplierStatus.homeworkComplete === true) {
      toast(t('practice.multiplierBonusTitle', { defaultValue: 'Bonus Points Active (×1.5)' }), {
        id: 'multiplier-toast',
        description: t('practice.multiplierBonusDesc', { defaultValue: 'All homework complete! Now is a great time to practice and earn bonus points.' }),
        duration: 5000,
        style: { backgroundColor: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d', width: '350px' },
        classNames: { description: '!text-green-800' },
      });
    }
  }, [isStudent, multiplierStatus, location.pathname, t]);

  // Show warning toast with countdown 10 minutes before platform closes (gamification students only)
  useEffect(() => {
    if (!isStudent || !gamificationEnabled) return;
    const endTimeStr = import.meta.env.VITE_ALLOWED_END_TIME;
    if (!endTimeStr) return;

    const [endHours, endMinutes] = endTimeStr.split(':').map(Number);
    const getEndTime = () => {
      const d = new Date();
      d.setHours(endHours, endMinutes, 0, 0);
      return d;
    };

    const getRemainingMinutes = () => Math.round((getEndTime().getTime() - Date.now()) / 60000);

    const now = new Date();
    const warningTime = new Date(now);
    warningTime.setHours(endHours, endMinutes, 0, 0);
    warningTime.setMinutes(warningTime.getMinutes() - 10);

    const msUntilWarning = warningTime.getTime() - now.getTime();

    if (msUntilWarning < -10 * 60 * 1000) return; // already past end time

    let intervalId: ReturnType<typeof setInterval> | undefined;

    const showWarning = () => {
      const remaining = getRemainingMinutes();
      if (remaining <= 0) {
        if (intervalId) clearInterval(intervalId);
        return;
      }
      toast.warning(t('gamification.timeWarningTitle', { defaultValue: 'Platform Closing Soon' }), {
        id: 'platform-closing-warning',
        description: t('gamification.timeWarningMessage', {
          defaultValue: 'The platform will close in {{minutes}} minutes. Please save any unsaved work.',
          minutes: remaining,
        }),
        duration: Infinity,
        style: { backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#92400e', width: '380px' },
        classNames: { description: '!text-amber-800' },
      });
    };

    const startCountdown = () => {
      showWarning();
      intervalId = setInterval(showWarning, 60000);
    };

    if (msUntilWarning <= 0) {
      startCountdown();
    } else {
      var timerId = setTimeout(startCountdown, msUntilWarning);
    }

    return () => {
      if (timerId) clearTimeout(timerId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [isStudent, gamificationEnabled, t]);

  // Fetch monthly progress lazily (called when KP or GS dialog opens)
  const fetchMonthlyProgress = useCallback(async () => {
    if (monthlyProgressFetched.current) return;
    monthlyProgressFetched.current = true;
    setIsMonthlyProgressLoading(true);
    try {
      const rsp = await getMonthlyProgress({ month: currentMonth });
      // TODO: Remove this debug log after verifying monthly progress data
      console.log('[DEBUG] Monthly progress response:', JSON.stringify(rsp, null, 2));
      if (rsp.code === 200 && rsp.data) {
        setMonthlyProgress(rsp.data);
      } else {
        // TODO: Remove this debug log after verifying monthly progress data
        console.warn('[DEBUG] Monthly progress unexpected response code or missing data:', rsp.code, rsp.data);
      }
    } catch (e) {
      console.warn('Failed to fetch monthly progress:', e);
    } finally {
      setIsMonthlyProgressLoading(false);
    }
  }, [currentMonth]);

  if (!gamificationEnabled || !isStudent) return null;

  return (
    /* 白色圆角数据卡（Figma 隨心練 右上角）：小屏收窄内边距 */
    <div className="rounded-[22px] bg-white px-3 py-2 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.25)] sm:rounded-[25px] sm:px-4 sm:py-2 min-[1920px]:rounded-[28px]! min-[1920px]:px-5! min-[1920px]:py-2.5! [@media(max-height:500px)]:h-[43px] [@media(max-height:500px)]:w-[216px] [@media(max-height:500px)]:rounded-[15px] [@media(max-height:500px)]:pl-5 [@media(max-height:500px)]:pr-2.5 [@media(max-height:500px)]:py-0 [@media(max-height:500px)]:shadow-[0_0_5px_rgba(0,0,0,0.10)]">
      <StudentGamificationScore
        kp={kp}
        gs={gs}
        kpAllTime={kpAllTime}
        gsAllTime={gsAllTime}
        multiplierStatus={multiplierStatus}
        monthlyProgress={monthlyProgress}
        isMonthlyProgressLoading={isMonthlyProgressLoading}
        currentMonth={currentMonth}
        onRequestMonthlyProgress={fetchMonthlyProgress}
      />
    </div>
  );
}

/**
 * 排行榜按钮 + 弹窗（Figma 设计稿要求移至顶栏控制行）。
 * 自带全部排行榜状态与数据拉取；教师 / 学生只要 leaderboardEnabled 即显示。
 * compact 用于 <1180px 折叠控制行的窄版样式。
 */
export function GamificationLeaderboard({ compact = false, legacy = false, children }: { compact?: boolean; legacy?: boolean; children?: ReactNode }) {
  const { t } = useTranslation();
  const { currentMonth: contextMonth, gamificationEnabled, leaderboardEnabled } = useGamification();

  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [masterData, setMasterData] = useState<LeaderboardData | null>(null);
  const [gritData, setGritData] = useState<LeaderboardData | null>(null);
  const [rocketData, setRocketData] = useState<LeaderboardData | null>(null);
  const [hofData, setHofData] = useState<LeaderboardData | null>(null);
  const [winnersData, setWinnersData] = useState<LeaderboardWinnersResponse | null>(null);
  const { isLoading: isLoadingLb, execute: executeLb } = useAsyncAction({ showErrorToast: false });

  const currentMonth = contextMonth || format(new Date(), 'yyyy-MM');

  const fetchLeaderboards = useCallback(() => {
    executeLb(async () => {
      const previousMonth = format(subMonths(new Date(currentMonth + '-01'), 1), 'yyyy-MM');
      try {
        const [masterRsp, gritRsp, rocketRsp, hofRsp, winnersRsp] = await Promise.all([
          getMasterLeaderboard({ month: currentMonth, page: 1, size: 3 }),
          getGritLeaderboard({ month: currentMonth, page: 1, size: 3 }),
          getRocketLeaderboard({ month: currentMonth, page: 1, size: 3 }),
          getHallOfFameLeaderboard({ page: 1, size: 3 }),
          getPreviousMonthWinners({ month: previousMonth }),
        ]);

        if (masterRsp.code === 200 && masterRsp.data) {
          setMasterData({ entries: masterRsp.data.entries ?? [], studentNames: masterRsp.data.studentNames, schoolNamesCn: masterRsp.data.schoolNamesCn });
        }
        if (gritRsp.code === 200 && gritRsp.data) {
          setGritData({ entries: gritRsp.data.entries ?? [], studentNames: gritRsp.data.studentNames, schoolNamesCn: gritRsp.data.schoolNamesCn });
        }
        if (rocketRsp.code === 200 && rocketRsp.data) {
          setRocketData({ entries: rocketRsp.data.entries ?? [], studentNames: rocketRsp.data.studentNames, schoolNamesCn: rocketRsp.data.schoolNamesCn });
        }
        if (hofRsp.code === 200 && hofRsp.data) {
          setHofData({ entries: hofRsp.data.entries ?? [], studentNames: hofRsp.data.studentNames, schoolNamesCn: hofRsp.data.schoolNamesCn });
        }
        if (winnersRsp.code === 200 && winnersRsp.data) {
          setWinnersData(winnersRsp.data);
        }
      } catch (error) {
        console.warn('Failed to fetch leaderboard data:', error);
      }
    });
  }, [currentMonth, executeLb]);

  useEffect(() => {
    if (leaderboardOpen && leaderboardEnabled) {
      fetchLeaderboards();
    }
  }, [leaderboardOpen, leaderboardEnabled, fetchLeaderboards]);

  if (!gamificationEnabled || !leaderboardEnabled) return null;

  return (
      <Dialog open={leaderboardOpen} onOpenChange={setLeaderboardOpen}>
        <DialogTrigger asChild>
          {children ? (
            // 自定义触发器（如手机横屏舞台的定制胶囊）：复用本组件的排行榜弹窗与数据拉取逻辑
            children
          ) : legacy ? (
            // 非学生（教师等）端：还原 github/dev 旧版排行榜按钮（绿色 bg-primary，文字 ≥1180px 才显示）
            <Button
              variant="outline"
              title={t('common.leaderboard')}
              className="animate-shimmer relative overflow-hidden group rounded-xl bg-primary border-2 border-background px-3 py-2 text-xs font-bold text-white transition-all hover:border-primary hover:-translate-y-1 hover:shadow-[4px_4px_0px_0px] hover:shadow-primary/60 hover:bg-primary/10 hover:text-primary active:translate-y-0 active:shadow-none"
            >
              <Trophy className="h-4 w-4 transition-transform group-hover:-translate-y-1 group-hover:rotate-[-8deg]" />
              <span className="font-bold font-yusei-magic hidden min-[1180px]:inline">{t('common.leaderboard')}</span>
            </Button>
          ) : (
            <button
              type="button"
              title={t('common.leaderboard')}
              className={
                compact
                  ? "inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1.5 text-xs font-medium text-[#1F2937] transition hover:brightness-95"
                  : "inline-flex items-center gap-1.5 rounded-full bg-accent px-4 py-2 text-sm font-medium text-[#1F2937] transition hover:brightness-95"
              }
            >
              <Trophy className="h-4 w-4" />
              {t('common.leaderboard')}
            </button>
          )}
        </DialogTrigger>
        <DialogContent
          showTitle={false}
          showCloseButton={false}
          /* 手机横屏(高≤500)：弹窗宽高 1:1 取自 Figma 844×390 稿(node 642-133 → 463×262)。
             只加 max-height:500px 覆盖,不动 1920/平板样式;!important 以压过 sm:max-w-3xl / max-h-[85vh]。 */
          className="font-sans bg-white border-0 p-5 sm:p-6 xl:p-7 rounded-3xl sm:max-w-3xl lg:max-w-4xl xl:max-w-5xl max-h-[85vh] overflow-y-auto [@media(max-height:500px)]:!w-[min(463px,calc(100vw-2rem))] [@media(max-height:500px)]:!max-w-[463px] [@media(max-height:500px)]:!h-[262px] [@media(max-height:500px)]:content-start [@media(max-height:500px)]:!px-5 [@media(max-height:500px)]:!py-4 [@media(max-height:500px)]:!gap-2 [@media(max-height:500px)]:!rounded-[18px]"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl font-medium text-foreground xl:text-2xl [@media(max-height:500px)]:gap-1.5 [@media(max-height:500px)]:text-sm [@media(max-height:500px)]:leading-none">
              <img src={TrophyIcon} alt="" aria-hidden className="h-5 w-auto xl:h-6 [@media(max-height:500px)]:h-[13px]" />
              {t('common.leaderboard')}
            </DialogTitle>
          </DialogHeader>
          {/* 显式关闭按钮：替代 DialogContent 默认 X，带本地化 aria-label */}
          <button
            type="button"
            onClick={() => setLeaderboardOpen(false)}
            className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:bg-black/5 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [@media(max-height:500px)]:right-3 [@media(max-height:500px)]:top-3"
            aria-label={t('common.close', { defaultValue: '關閉' })}
            title={t('common.close', { defaultValue: '關閉' })}
          >
            <X className="h-4 w-4" />
          </button>
          <Tabs defaultValue="school" className="w-full gap-4 [@media(max-height:500px)]:gap-2">
            {/* 排行榜弹窗胶囊 tab：选中态使用浅绿底色 + 加粗文字，降低视觉重量。 */}
            <TabsList className="h-12 w-full rounded-full bg-gray-100 p-1 items-center overflow-visible xl:h-14 [@media(max-height:500px)]:h-8">
              <TabsTrigger
                value="school"
                className="!w-auto !max-w-none flex-1 !mx-0 !h-full !rounded-full !bg-transparent text-sm font-medium text-gray-500 hover:!bg-gray-200/60 data-[state=active]:!bg-[#ECFCCB] data-[state=active]:!text-lime-800 data-[state=active]:!font-semibold data-[state=active]:!shadow-none after:!bg-transparent xl:text-base [@media(max-height:500px)]:!text-xs"
              >
                {t('common.schoolLeaderboard')}
              </TabsTrigger>
              <TabsTrigger
                value="legends"
                className="!w-auto !max-w-none flex-1 !mx-0 !h-full !rounded-full !bg-transparent text-sm font-medium text-gray-500 hover:!bg-gray-200/60 data-[state=active]:!bg-[#ECFCCB] data-[state=active]:!text-lime-800 data-[state=active]:!font-semibold data-[state=active]:!shadow-none after:!bg-transparent xl:text-base [@media(max-height:500px)]:!text-xs"
              >
                {t('gamification.rules.hallOfFameTitle')}
              </TabsTrigger>
              <TabsTrigger
                value="winners"
                className="!w-auto !max-w-none flex-1 !mx-0 !h-full !rounded-full !bg-transparent text-sm font-medium text-gray-500 hover:!bg-gray-200/60 data-[state=active]:!bg-[#ECFCCB] data-[state=active]:!text-lime-800 data-[state=active]:!font-semibold data-[state=active]:!shadow-none after:!bg-transparent xl:text-base [@media(max-height:500px)]:!text-xs"
              >
                {t('common.lastMonthWinners')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="school" className="pt-0 space-y-3 xl:space-y-4 [@media(max-height:500px)]:space-y-1">
              <LeaderboardSection
                title={t('gamification.rules.masterLeagueTitle')}
                data={masterData}
                isLoading={isLoadingLb}
                scoreKey="totalKp"
              />
              <LeaderboardSection
                title={t('gamification.rules.gritLeagueTitle')}
                data={gritData}
                isLoading={isLoadingLb}
                scoreKey="totalGs"
              />
              <LeaderboardSection
                title={t('gamification.rules.rocketLeagueTitle')}
                data={rocketData}
                isLoading={isLoadingLb}
                scoreKey="improvementScore"
              />
            </TabsContent>
            <TabsContent value="legends" className="pt-0">
              <LeaderboardSection
                title={t('gamification.rules.hallOfFameTitle')}
                data={hofData}
                isLoading={isLoadingLb}
                scoreKey="totalPoints"
                isHallOfFame
                topN={3}
                /* 手机横屏传奇榜：卡片上下留白 + 标题→行间距按 Figma 642-132 加大(上15/间9/下6) */
                className="[@media(max-height:500px)]:!pt-[15px] [@media(max-height:500px)]:!pb-[6px] [@media(max-height:500px)]:!space-y-[9px]"
              />
            </TabsContent>
            <TabsContent value="winners" className="pt-0">
              <div className="rounded-2xl bg-background px-4 py-3.5 xl:px-5 xl:py-4 [@media(max-height:500px)]:rounded-[10px] [@media(max-height:500px)]:px-2 [@media(max-height:500px)]:py-1">
                {isLoadingLb ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground [@media(max-height:500px)]:py-3">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  </div>
                ) : !winnersData || ([winnersData.master, winnersData.grit, winnersData.rocket, winnersData.hallOfFame].every(arr => !arr?.length)) ? (
                  <div className="py-8 text-center text-xs text-muted-foreground [@media(max-height:500px)]:py-3">{t('common.noDataYet')}</div>
                ) : (
                  <>
                    {winnersData.seasonMonth && (
                      <div className="mb-3 text-sm font-medium text-foreground xl:mb-4 xl:text-base [@media(max-height:500px)]:mb-1 [@media(max-height:500px)]:text-xs [@media(max-height:500px)]:leading-none">
                        {winnersData.seasonMonth}
                      </div>
                    )}
                    <div className="overflow-x-auto">
                      {/* 手机横屏：table-fixed 四榜列等宽(空列不塌陷)；表头 whitespace-nowrap 强制单行、
                          姓名 break-words 换行撑高本行、名次奖牌 align-middle 垂直居中；超高时弹窗竖向滚动 */}
                      <table className="w-full text-sm [@media(max-height:500px)]:table-fixed">
                        <thead>
                          <tr className="text-xs text-muted-foreground xl:text-sm">
                            <th className="py-2 pr-3 text-left font-normal [@media(max-height:500px)]:w-7 [@media(max-height:500px)]:whitespace-nowrap [@media(max-height:500px)]:py-0.5 [@media(max-height:500px)]:pr-1.5">{t('common.rank')}</th>
                            <th className="py-2 px-3 text-left font-normal [@media(max-height:500px)]:whitespace-nowrap [@media(max-height:500px)]:py-0.5 [@media(max-height:500px)]:px-1.5">{t('gamification.rules.masterLeagueTitle')}</th>
                            <th className="py-2 px-3 text-left font-normal [@media(max-height:500px)]:whitespace-nowrap [@media(max-height:500px)]:py-0.5 [@media(max-height:500px)]:px-1.5">{t('gamification.rules.gritLeagueTitle')}</th>
                            <th className="py-2 px-3 text-left font-normal [@media(max-height:500px)]:whitespace-nowrap [@media(max-height:500px)]:py-0.5 [@media(max-height:500px)]:px-1.5">{t('gamification.rules.rocketLeagueTitle')}</th>
                            <th className="py-2 pl-3 text-right font-normal [@media(max-height:500px)]:whitespace-nowrap [@media(max-height:500px)]:py-0.5 [@media(max-height:500px)]:pl-1.5">{t('gamification.rules.hallOfFameTitle')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {([1, 2, 3] as LeaderboardRank[]).map((rank) => {
                            const master = winnersData.master?.[rank - 1];
                            const grit = winnersData.grit?.[rank - 1];
                            const rocket = winnersData.rocket?.[rank - 1];
                            const hof = winnersData.hallOfFame?.[rank - 1];
                            return (
                              <tr key={rank} className="align-top [@media(max-height:500px)]:align-middle">
                                <td className="py-3 pr-3 [@media(max-height:500px)]:py-1 [@media(max-height:500px)]:pr-1.5">
                                  <LeaderboardMedal rank={rank} />
                                </td>
                                <td className="py-3 px-3 text-sm xl:text-base [@media(max-height:500px)]:py-1 [@media(max-height:500px)]:px-1.5 [@media(max-height:500px)]:text-xs">
                                  {master ? (
                                    <div className="text-foreground">
                                      <div className="[@media(max-height:500px)]:break-words">{winnersData.studentNames?.[String(master.studentId)] ?? `Student #${master.studentId}`}</div>
                                      <div className="text-xs text-muted-foreground [@media(max-height:500px)]:leading-none">{master.totalKp ?? 0} KP</div>
                                    </div>
                                  ) : <span className="text-muted-foreground/40">—</span>}
                                </td>
                                <td className="py-3 px-3 text-sm xl:text-base [@media(max-height:500px)]:py-1 [@media(max-height:500px)]:px-1.5 [@media(max-height:500px)]:text-xs">
                                  {grit ? (
                                    <div className="text-foreground">
                                      <div className="[@media(max-height:500px)]:break-words">{winnersData.studentNames?.[String(grit.studentId)] ?? `Student #${grit.studentId}`}</div>
                                      <div className="text-xs text-muted-foreground [@media(max-height:500px)]:leading-none">{grit.totalGs ?? 0} GS</div>
                                    </div>
                                  ) : <span className="text-muted-foreground/40">—</span>}
                                </td>
                                <td className="py-3 px-3 text-sm xl:text-base [@media(max-height:500px)]:py-1 [@media(max-height:500px)]:px-1.5 [@media(max-height:500px)]:text-xs">
                                  {rocket ? (
                                    <div className="text-foreground">
                                      <div className="[@media(max-height:500px)]:break-words">{winnersData.studentNames?.[String(rocket.studentId)] ?? `Student #${rocket.studentId}`}</div>
                                      <div className="text-xs text-muted-foreground [@media(max-height:500px)]:leading-none">{rocket.improvementScore ?? 0}</div>
                                    </div>
                                  ) : <span className="text-muted-foreground/40">—</span>}
                                </td>
                                <td className="py-3 pl-3 text-right text-sm xl:text-base [@media(max-height:500px)]:py-1 [@media(max-height:500px)]:pl-1.5 [@media(max-height:500px)]:text-xs">
                                  {hof ? (
                                    <div className="text-foreground">
                                      <div className="[@media(max-height:500px)]:break-words">{winnersData.studentNames?.[String(hof.studentId)] ?? `Student #${hof.studentId}`}</div>
                                      <div className="text-xs text-muted-foreground [@media(max-height:500px)]:leading-none">{hof.totalPoints ?? 0} pts</div>
                                    </div>
                                  ) : <span className="text-muted-foreground/40">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
  );
}
